import { useState } from "react";
import { List, LayoutGrid, ChevronRight } from "lucide-react";
import { storage } from "@/lib/storage";
import { GroupHeader } from "@/components/shared/GroupSort";

// 317: Investoři a Dodavatelé — pohled Seznam (výchozí) nebo Karty, volba se pamatuje.
// Seznam: na širokém PC (xl) tabulka se sloupci, na telefonu a tabletu řádky (název, pod ním údaje, vpravo počet).

const VIEWS = [
  { value: "list", label: "Seznam", icon: List },
  { value: "cards", label: "Karty", icon: LayoutGrid },
];

export function useAdresarView(key) {
  const [view, setViewState] = useState(() => (storage.get(key) === "cards" ? "cards" : "list"));
  const setView = (v) => { setViewState(v); storage.set(key, v); };
  return [view, setView];
}

export function ViewSwitch({ value, onChange, testid = "adresar-views" }) {
  return (
    <div className="flex h-11 p-1 bg-card border border-border rounded-full shrink-0" role="radiogroup" aria-label="Pohled" data-testid={testid}>
      {VIEWS.map((v) => (
        <button key={v.value} type="button" role="radio" aria-checked={value === v.value} aria-label={v.label} title={v.label}
          onClick={() => onChange(v.value)} data-testid={`${testid}-${v.value}`}
          className={`h-full w-10 rounded-full grid place-items-center transition-colors motion-reduce:transition-none ${value === v.value ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}>
          <v.icon size={17} />
        </button>
      ))}
    </div>
  );
}

/**
 * cols: [{ label, width, cell(row), right }] — PC tabulka.
 * mobile(row) → { title, sub, side } — řádek na telefonu.
 * 378: groups / closed / onToggleGroup = skupiny ze Seskupit a řadit (lišta skupiny nad jejími řádky, klik sbalí).
 */
export function AdresarSeznam({ rows, cols, mobile, onOpen, rowTestid, testid, groups = null, closed, onToggleGroup }) {
  const grid = { gridTemplateColumns: cols.map((c) => c.width || "1fr").join(" ") };
  const open = (r) => (e) => { if (e.type === "click" || e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(r); } };
  return (
    <div className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid={testid}>
      <div className="hidden xl:grid items-center gap-4 px-5 bg-secondary/60 border-b border-border" style={grid}>
        {cols.map((c, i) => (
          <span key={i} className={`min-h-[44px] flex items-center text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground ${c.right ? "justify-end" : ""}`}>{c.label}</span>
        ))}
      </div>
      {(() => {
      const rowEl = (r) => {
        const m = mobile(r);
        return (
          <div key={r.id} role="button" tabIndex={0} onClick={open(r)} onKeyDown={open(r)} data-testid={rowTestid(r)}
            className="cursor-pointer border-b border-border last:border-b-0 hover:bg-secondary/50 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:bg-secondary/60">
            <div className="hidden xl:grid items-center gap-4 px-5 py-3" style={grid}>
              {cols.map((c, i) => <div key={i} className={`min-w-0 text-sm ${c.right ? "text-right" : ""}`}>{c.cell(r)}</div>)}
            </div>
            <div className="xl:hidden flex items-center gap-3 px-4 md:px-5 py-3 min-h-[60px]">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-[15px] truncate">{m.title}</div>
                {m.sub && <div className="text-[13px] text-muted-foreground truncate">{m.sub}</div>}
              </div>
              {m.side && <div className="shrink-0 text-[13px] text-muted-foreground tabular-nums">{m.side}</div>}
              <ChevronRight size={16} className="text-muted-foreground shrink-0" />
            </div>
          </div>
        );
      };
      return groups ? groups.map((g) => [<GroupHeader key={`g-${g.key}`} g={g} closed={closed?.has(g.key)} onToggle={onToggleGroup} />, ...(closed?.has(g.key) ? [] : g.rows.map(rowEl))]) : rows.map(rowEl);
      })()}
    </div>
  );
}

/** Kontakt do buňky: e-mail a pod ním telefon, nebo pomlčka. */
export function KontaktBunka({ email, phone }) {
  if (!email && !phone) return <span className="text-muted-foreground">—</span>;
  return (
    <>
      {email && <span className="block truncate">{email}</span>}
      {phone && <span className="block truncate text-muted-foreground tabular-nums">{phone}</span>}
    </>
  );
}
